import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { isNumber, Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { ITag } from 'app/shared/model/tag.model';
import { getEntities as getTags } from 'app/entities/tag/tag.reducer';
import { ITourExtraCategory } from 'app/shared/model/tour-extra-category.model';
import { getEntities as getTourExtraCategories } from 'app/entities/tour-extra-category/tour-extra-category.reducer';
import { ITour } from 'app/shared/model/tour.model';
import { getEntities as getTours } from 'app/entities/tour/tour.reducer';
import { getEntity, updateEntity, createEntity, reset } from './tour-extra.reducer';

const TourExtraUpdate = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const account = useAppSelector(state => state.authentication.account);
  const tags = useAppSelector(state => state.tag.entities);
  const tourExtraCategories = useAppSelector(state => state.tourExtraCategory.entities);
  const tours = useAppSelector(state => state.tour.entities);
  const tourExtraEntity = useAppSelector(state => state.tourExtra.entity);
  const loading = useAppSelector(state => state.tourExtra.loading);
  const updating = useAppSelector(state => state.tourExtra.updating);
  const updateSuccess = useAppSelector(state => state.tourExtra.updateSuccess);

  const [formData, setFormData] = useState<any>({});

  const handleClose = () => {
    navigate('/tour-extra' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getTags({}));
    dispatch(getTourExtraCategories({}));
    dispatch(getTours({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const getInitialFormData = () => {
    if (!isNew && tourExtraEntity) {
      return {
        ...tourExtraEntity,
        createdBy: tourExtraEntity?.createdBy?.id,
        tags: tourExtraEntity?.tags?.map((e: any) => e.id.toString()),
        categories: tourExtraEntity?.categories?.map((e: any) => e.id.toString()),
        tours: tourExtraEntity?.tours?.map((e: any) => e.id.toString()),
      };
    }
    return {
      createdBy: account?.id,
    };
  };

  useEffect(() => {
    setFormData(getInitialFormData());
  }, [tourExtraEntity, isNew]);

  const handleChange = (field: string) => (event: any) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleMultiSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const convertToNumber = (value: any, field: string): any => {
    const numberFields = ['id', 'price', 'offer'];
    if (numberFields.includes(field) && value !== undefined && typeof value !== 'number') {
      return Number(value);
    }
    return value;
  };

  const findEntityById = (entities: any[], value: any) => {
    return entities.find(it => it.id.toString() === value?.toString());
  };

  const saveEntity = () => {
    const values = { ...formData };
    ['id', 'price', 'offer'].forEach(field => {
      if (values[field] !== undefined) {
        values[field] = convertToNumber(values[field], field);
      }
    });

    values.enabled = values.enabled === true;

    const entity = {
      ...tourExtraEntity,
      ...values,
      createdBy: findEntityById(users, values.createdBy),
      tags: values.tags ? mapIdList(values.tags) : [],
      categories: values.categories ? mapIdList(values.categories) : [],
      tours: values.tours ? mapIdList(values.tours) : [],
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <h2 id="jhipsterSampleApplicationApp.tourExtra.home.createOrEditLabel" data-cy="TourExtraCreateUpdateHeading">
          <Translate
            contentKey={
              isNew ? 'jhipsterSampleApplicationApp.tourExtra.home.createLabel' : 'jhipsterSampleApplicationApp.tourExtra.home.editLabel'
            }
          >
            {isNew ? 'Create Tour Extra' : 'Edit Tour Extra'}
          </Translate>
        </h2>
        {!isNew && formData.id && (
          <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', px: 2, py: 0.5, borderRadius: 1, fontWeight: 'bold' }}>
            ID: {formData.id}
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>Loading...</Box>
      ) : (
        <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 8 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tourExtra.code')}
                    id="tour-extra-code"
                    name="code"
                    data-cy="code"
                    value={formData.code || ''}
                    onChange={handleChange('code')}
                    fullWidth
                    required
                    error={!formData.code}
                    helperText={!formData.code ? translate('entity.validation.required') : ''}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enabled || false}
                        onChange={handleChange('enabled')}
                        id="tour-extra-enabled"
                        name="enabled"
                        data-cy="enabled"
                      />
                    }
                    label={translate('jhipsterSampleApplicationApp.tourExtra.enabled')}
                  />
                </Grid>
              </Grid>

              <TextField
                label={translate('jhipsterSampleApplicationApp.tourExtra.icon')}
                id="tour-extra-icon"
                name="icon"
                data-cy="icon"
                value={formData.icon || ''}
                onChange={handleChange('icon')}
                fullWidth
              />

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tourExtra.price')}
                    id="tour-extra-price"
                    name="price"
                    data-cy="price"
                    value={formData.price || ''}
                    onChange={handleChange('price')}
                    fullWidth
                    type="number"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label={translate('jhipsterSampleApplicationApp.tourExtra.offer')}
                    id="tour-extra-offer"
                    name="offer"
                    data-cy="offer"
                    value={formData.offer || ''}
                    onChange={handleChange('offer')}
                    fullWidth
                    type="number"
                  />
                </Grid>
              </Grid>

              <TextField
                label={translate('jhipsterSampleApplicationApp.tourExtra.shopProductId')}
                id="tour-extra-shopProductId"
                name="shopProductId"
                data-cy="shopProductId"
                value={formData.shopProductId || ''}
                onChange={handleChange('shopProductId')}
                fullWidth
              />

              <TextField
                label={translate('jhipsterSampleApplicationApp.tourExtra.shopUrl')}
                id="tour-extra-shopUrl"
                name="shopUrl"
                data-cy="shopUrl"
                value={formData.shopUrl || ''}
                onChange={handleChange('shopUrl')}
                fullWidth
              />

              <TextField
                label={translate('jhipsterSampleApplicationApp.tourExtra.defaultImage')}
                id="tour-extra-defaultImage"
                name="defaultImage"
                data-cy="defaultImage"
                value={formData.defaultImage || ''}
                onChange={handleChange('defaultImage')}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="tour-extra-tags-label">{translate('jhipsterSampleApplicationApp.tourExtra.tags')}</InputLabel>
                <Select
                  labelId="tour-extra-tags-label"
                  id="tour-extra-tags"
                  data-cy="tags"
                  multiple
                  value={formData.tags || []}
                  onChange={handleMultiSelectChange('tags')}
                  label={translate('jhipsterSampleApplicationApp.tourExtra.tags')}
                  renderValue={selected => {
                    const selectedTags = tags?.filter((t: any) => selected.includes(t.id.toString())) || [];
                    return selectedTags.map((t: any) => t.code).join(', ');
                  }}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {tags
                    ? tags.map(otherEntity => (
                        <MenuItem value={otherEntity.id} key={otherEntity.id}>
                          {otherEntity.code}
                        </MenuItem>
                      ))
                    : null}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="tour-extra-category-label">{translate('jhipsterSampleApplicationApp.tourExtra.category')}</InputLabel>
                <Select
                  labelId="tour-extra-category-label"
                  id="tour-extra-category"
                  data-cy="category"
                  multiple
                  value={formData.categories || []}
                  onChange={handleMultiSelectChange('categories')}
                  label={translate('jhipsterSampleApplicationApp.tourExtra.category')}
                  renderValue={selected => {
                    const selectedCategories = tourExtraCategories?.filter((c: any) => selected.includes(c.id.toString())) || [];
                    return selectedCategories.map((c: any) => c.code).join(', ');
                  }}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {tourExtraCategories
                    ? tourExtraCategories.map(otherEntity => (
                        <MenuItem value={otherEntity.id} key={otherEntity.id}>
                          {otherEntity.code}
                        </MenuItem>
                      ))
                    : null}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="tour-extra-tours-label">{translate('jhipsterSampleApplicationApp.tourExtra.tours')}</InputLabel>
                <Select
                  labelId="tour-extra-tours-label"
                  id="tour-extra-tours"
                  data-cy="tours"
                  multiple
                  value={formData.tours || []}
                  onChange={handleMultiSelectChange('tours')}
                  label={translate('jhipsterSampleApplicationApp.tourExtra.tours')}
                  renderValue={selected => {
                    const selectedTours = tours?.filter((t: any) => selected.includes(t.id.toString())) || [];
                    return selectedTours.map((t: any) => t.code).join(', ');
                  }}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {tours
                    ? tours.map(otherEntity => (
                        <MenuItem value={otherEntity.id} key={otherEntity.id}>
                          {otherEntity.code}
                        </MenuItem>
                      ))
                    : null}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  component={Link}
                  id="cancel-save"
                  data-cy="entityCreateCancelButton"
                  to="/tour-extra"
                  replace
                  color="info"
                  variant="outlined"
                  startIcon={<FontAwesomeIcon icon="arrow-left" />}
                >
                  <Translate contentKey="entity.action.back">Back</Translate>
                </Button>
                <Button
                  color="primary"
                  id="save-entity"
                  data-cy="entityCreateSaveButton"
                  onClick={saveEntity}
                  disabled={updating}
                  variant="contained"
                  startIcon={<FontAwesomeIcon icon="save" />}
                >
                  <Translate contentKey="entity.action.save">Save</Translate>
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default TourExtraUpdate;
